import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { 
  Users, 
  Server, 
  History,
  Lock,
  Unlock,
  UserPlus
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminPage = () => {
  const { t } = useLanguage();
  const { isAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [devices, setDevices] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const [usersRes, devicesRes, logsRes] = await Promise.all([
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/admin/devices`),
        axios.get(`${API}/admin/logs`)
      ]);
      setUsers(usersRes.data);
      setDevices(devicesRes.data);
      setLogs(logsRes.data);
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      fetchData();
    }
  }, [isAdmin]);

  const handleAssignDevice = async (deviceId, userId) => {
    try {
      await axios.post(`${API}/admin/devices/assign`, {
        device_id: deviceId,
        user_id: userId || null
      });
      toast.success('Device assignment updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to assign device');
    }
  };

  const handleToggleLock = async (deviceId, isLocked) => {
    try {
      await axios.post(`${API}/admin/devices/lock`, {
        device_id: deviceId,
        is_locked: !isLocked
      });
      toast.success(isLocked ? 'Device unlocked' : 'Device locked');
      fetchData();
    } catch (error) {
      toast.error('Failed to toggle device lock');
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString('zh-TW');
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-fac-dark pt-16 flex items-center justify-center">
        <div className="text-center">
          <Lock className="w-16 h-16 text-fac-red mx-auto mb-4" />
          <p className="text-xl text-white font-chinese">
            Admin access required / 需要管理員權限
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-fac-dark pt-16 pb-24 md:pb-8">
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-6">
        {/* Header */}
        <h1 className="font-heading font-bold text-3xl text-white uppercase mb-6" data-testid="admin-title">
          {t('admin_title')}
          <span className="block text-fac-green font-chinese text-xl mt-1">管理面板</span>
        </h1>

        {/* Tabs */}
        <Tabs defaultValue="users" className="space-y-6">
          <TabsList className="bg-fac-card border border-border">
            <TabsTrigger value="users" className="data-[state=active]:bg-fac-green data-[state=active]:text-black">
              <Users className="w-4 h-4 mr-2" />
              {t('admin_users')}
            </TabsTrigger>
            <TabsTrigger value="devices" className="data-[state=active]:bg-fac-green data-[state=active]:text-black">
              <Server className="w-4 h-4 mr-2" />
              {t('admin_devices')}
            </TabsTrigger>
            <TabsTrigger value="logs" className="data-[state=active]:bg-fac-green data-[state=active]:text-black">
              <History className="w-4 h-4 mr-2" />
              {t('admin_logs')}
            </TabsTrigger>
          </TabsList>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card className="bg-fac-card border-border">
              <CardHeader>
                <CardTitle className="font-heading text-white uppercase flex items-center gap-2">
                  <Users className="w-5 h-5 text-fac-green" />
                  All Users / 所有使用者
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="animate-pulse space-y-3">
                    {[1, 2, 3].map(i => (
                      <div key={i} className="h-16 bg-muted rounded" />
                    ))}
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full" data-testid="users-table">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="text-left py-3 px-4 text-muted-foreground font-medium">Name / 姓名</th>
                          <th className="text-left py-3 px-4 text-muted-foreground font-medium">Email</th>
                          <th className="text-left py-3 px-4 text-muted-foreground font-medium">Role</th>
                          <th className="text-left py-3 px-4 text-muted-foreground font-medium">Created</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.map((user) => (
                          <tr key={user.id} className="border-b border-border hover:bg-fac-paper">
                            <td className="py-3 px-4 text-white">{user.name}</td>
                            <td className="py-3 px-4 text-muted-foreground">{user.email}</td>
                            <td className="py-3 px-4">
                              <span className={`px-2 py-1 rounded text-xs font-bold uppercase ${
                                user.role === 'admin' ? 'bg-fac-green/20 text-fac-green' : 'bg-fac-blue/20 text-fac-blue'
                              }`}>
                                {user.role}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-muted-foreground text-sm">
                              {formatDate(user.created_at)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Devices Tab */}
          <TabsContent value="devices">
            <Card className="bg-fac-card border-border">
              <CardHeader>
                <CardTitle className="font-heading text-white uppercase flex items-center gap-2">
                  <Server className="w-5 h-5 text-fac-green" />
                  All Devices / 所有設備
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="animate-pulse space-y-3">
                    {[1, 2, 3].map(i => (
                      <div key={i} className="h-16 bg-muted rounded" />
                    ))}
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full" data-testid="devices-table">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="text-left py-3 px-4 text-muted-foreground font-medium">Device / 設備</th>
                          <th className="text-left py-3 px-4 text-muted-foreground font-medium">Serial</th>
                          <th className="text-left py-3 px-4 text-muted-foreground font-medium">Owner</th>
                          <th className="text-left py-3 px-4 text-muted-foreground font-medium">Status</th>
                          <th className="text-left py-3 px-4 text-muted-foreground font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {devices.map((device) => (
                          <tr key={device.id} className="border-b border-border hover:bg-fac-paper">
                            <td className="py-3 px-4">
                              <p className="text-white font-medium">{device.name}</p>
                              <p className="text-xs text-muted-foreground">{device.location}</p>
                            </td>
                            <td className="py-3 px-4 text-muted-foreground font-mono text-sm">
                              {device.serial_number}
                            </td>
                            <td className="py-3 px-4">
                              <Select
                                value={device.owner_id || ''}
                                onValueChange={(value) => handleAssignDevice(device.id, value)}
                              >
                                <SelectTrigger className="w-40 bg-fac-dark border-border text-white text-sm">
                                  <SelectValue placeholder="Unassigned" />
                                </SelectTrigger>
                                <SelectContent className="bg-fac-card border-border">
                                  <SelectItem value="">Unassigned</SelectItem>
                                  {users.map((user) => (
                                    <SelectItem key={user.id} value={user.id}>
                                      {user.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </td>
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-2">
                                <span className={`w-2 h-2 rounded-full ${
                                  device.status === 'online' ? 'bg-fac-green' : 'bg-fac-red'
                                }`} />
                                <span className="text-sm text-muted-foreground capitalize">
                                  {device.status}
                                </span>
                                {device.is_locked && (
                                  <Lock className="w-3 h-3 text-fac-red" />
                                )}
                              </div>
                            </td>
                            <td className="py-3 px-4">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleToggleLock(device.id, device.is_locked)}
                                className={device.is_locked ? 'text-fac-green' : 'text-fac-red'}
                              >
                                {device.is_locked ? (
                                  <>
                                    <Unlock className="w-4 h-4 mr-1" />
                                    {t('admin_unlock')}
                                  </>
                                ) : (
                                  <>
                                    <Lock className="w-4 h-4 mr-1" />
                                    {t('admin_lock')}
                                  </>
                                )}
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Logs Tab */}
          <TabsContent value="logs">
            <Card className="bg-fac-card border-border">
              <CardHeader>
                <CardTitle className="font-heading text-white uppercase flex items-center gap-2">
                  <History className="w-5 h-5 text-fac-green" />
                  System Logs / 系統日誌
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="animate-pulse space-y-3">
                    {[1, 2, 3, 4, 5].map(i => (
                      <div key={i} className="h-12 bg-muted rounded" />
                    ))}
                  </div>
                ) : (
                  <div className="space-y-2 max-h-96 overflow-y-auto" data-testid="admin-logs">
                    {logs.slice(0, 50).map((log) => (
                      <div
                        key={log.id}
                        className="flex items-start gap-3 p-3 bg-fac-paper rounded border border-border text-sm"
                      >
                        <span className="text-muted-foreground whitespace-nowrap">
                          {formatDate(log.created_at)}
                        </span>
                        <span className={`font-mono px-2 py-0.5 rounded text-xs ${
                          log.action.includes('lock') ? 'bg-fac-red/20 text-fac-red' :
                          log.action.includes('assign') ? 'bg-fac-blue/20 text-fac-blue' :
                          'bg-fac-green/20 text-fac-green'
                        }`}>
                          {log.action}
                        </span>
                        <span className="text-white flex-1">{log.details}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminPage;
